import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  useTheme,
  alpha,
  Fade,
  Divider,
  Tooltip,
} from "@mui/material";
import regionsData from "@/app/features/company/data/regions_lite.json";
import citiesData from "@/app/features/company/data/cities_lite.json";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  StarBorder as StarBorderIcon,
  LocationOn,
  Person,
  Phone,
  Home,
} from "@mui/icons-material";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { useShipping } from "@/hooks/use-shipping";
import { LANG } from "@/types";

interface Address {
  id: string;
  fullName: string;
  address: string;
  region: string;
  city: string;
  phoneNumber: string;
  isDefault: boolean;
}

interface Region {
  region_id: number;
  name_ar: string;
}

interface City {
  city_id: number;
  region_id: number;
  name_ar: string;
}

interface Option {
  value: string;
  label: string;
}

function ShippingPage() {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const theme = useTheme();
  const {
    myShipping,
    createShipping,
    updateShipping,
    deleteShipping,
    setDefaultShipping,
  } = useShipping();
  const [loading, setLoading] = useState(false);

  const getRegionLabel = (regionId: string) => {
    const region = regionsData.find(
      (r: Region) => r.region_id.toString() === regionId
    );
    return region ? region.name_ar : regionId;
  };

  const getCityLabel = (cityId: string) => {
    const city = citiesData.find((c: City) => c.city_id.toString() === cityId);
    return city ? city.name_ar : cityId;
  };

  const regions = useMemo(
    () =>
      regionsData.map((region: Region) => ({
        value: region.region_id.toString(),
        label: region.name_ar,
      })),
    []
  );
  const [cities, setCities] = useState<Option[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentAddress, setCurrentAddress] = useState<Address | null>(null);
  const [newAddress, setNewAddress] = useState<Partial<Address>>({
    fullName: "",
    address: "",
    region: "",
    city: "",
    phoneNumber: "",
    isDefault: false,
  });

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const data = await myShipping();
      if (data) {
        setAddresses(data);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAddress = async () => {
    try {
      setLoading(true);
      const response = await createShipping({
        fullName: newAddress.fullName || "",
        address: newAddress.address || "",
        region: newAddress.region || "",
        phoneNumber: newAddress.phoneNumber || "",
        city: newAddress.city || "",
        isDefault: newAddress.isDefault || false,
      });

      if (response) {
        await fetchAddresses();
        setIsAddDialogOpen(false);
        setNewAddress({
          fullName: "",
          address: "",
          region: "",
          city: "",
          phoneNumber: "",
          isDefault: false,
        });
      }
    } catch (error) {
      console.error("Error adding address:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAddress = async () => {
    if (!currentAddress) return;

    try {
      setLoading(true);
      const response = await updateShipping(currentAddress.id, {
        fullName: currentAddress.fullName,
        address: currentAddress.address,
        phoneNumber: currentAddress.phoneNumber,
        region: currentAddress.region,
        city: currentAddress.city,
        isDefault: currentAddress.isDefault,
      });

      if (response) {
        await fetchAddresses();
        setIsEditDialogOpen(false);
        setCurrentAddress(null);
      }
    } catch (error) {
      console.error("Error updating address:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (id: string) => {
    try {
      setLoading(true);
      const response = await deleteShipping(id);
      if (response) {
        await fetchAddresses();
      }
    } catch (error) {
      console.error("Error deleting address:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      setLoading(true);
      const response = await setDefaultShipping(id);
      if (response) {
        await fetchAddresses();
      }
    } catch (error) {
      console.error("Error setting default address:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const selectedRegion = newAddress.region;
    if (selectedRegion) {
      const filteredCities = citiesData
        .filter((city: City) => city.region_id === parseInt(selectedRegion))
        .map((city: City) => ({
          value: city?.city_id.toString(),
          label: city.name_ar,
        }));
      setCities(filteredCities);
    } else {
      setCities([]);
    }
  }, [newAddress.region]);

  useEffect(() => {
    if (currentAddress?.region) {
      const filteredCities = citiesData
        .filter(
          (city: City) => city.region_id === parseInt(currentAddress.region)
        )
        .map((city: City) => ({
          value: city?.city_id.toString(),
          label: city.name_ar,
        }));
      setCities(filteredCities);
    }
  }, [currentAddress?.region]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header Section */}
      <Card
        elevation={2}
        sx={{
          mb: 3,
          boxShadow: "none",
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <LocationOn color="primary" sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  {t("delivery.title")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("delivery.manageAddresses") ||
                    "Manage your shipping addresses"}
                </Typography>
              </Box>
            </Box>
            <Button
              className="!bg-[#18181B] !text-white"
              startIcon={<AddIcon />}
              onClick={() => setIsAddDialogOpen(true)}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {t("delivery.addNewAddress")}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Addresses Grid */}
      {addresses.length === 0 ? (
        <Fade in={true}>
          <Card
            elevation={2}
            sx={{
              boxShadow: "none",
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 6, textAlign: "center" }}>
              <LocationOn
                sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t("delivery.noAddresses") || "No addresses found"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t("delivery.addFirstAddress") ||
                  "Add your first shipping address to get started"}
              </Typography>
              <Button
                variant="outlined"
                className="!border-[#18181B] !text-[#18181B]"
                startIcon={<AddIcon />}
                onClick={() => setIsAddDialogOpen(true)}
                sx={{ borderRadius: 2 }}
              >
                {t("delivery.addNewAddress")}
              </Button>
            </CardContent>
          </Card>
        </Fade>
      ) : (
        <Grid container spacing={3}>
          {addresses.map((address) => (
            <Grid item xs={12} md={6} lg={4} key={address.id}>
              <Fade in={true}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    height: "100%",
                    position: "relative",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      elevation: 4,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {address.isDefault && (
                    <Chip
                      label={t("delivery.default") || "Default"}
                      color="primary"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        zIndex: 1,
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Person color="primary" sx={{ fontSize: 20 }} />
                      <Typography variant="h6" fontWeight={600}>
                        {address.fullName}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Home color="action" sx={{ fontSize: 18, mt: 0.2 }} />
                      <Typography variant="body2" color="text.secondary">
                        {address.address}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <LocationOn color="action" sx={{ fontSize: 18 }} />
                      <Typography variant="body2" color="text.secondary">
                        {getRegionLabel(address.region)},{" "}
                        {getCityLabel(address.city)}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 3,
                      }}
                    >
                      <Phone color="action" sx={{ fontSize: 18 }} />
                      <Typography variant="body2" color="text.secondary">
                        {address.phoneNumber}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title={t("actions.edit")}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setCurrentAddress(address);
                              setIsEditDialogOpen(true);
                            }}
                            sx={{
                              color: theme.palette.primary.main,
                              "&:hover": {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.1
                                ),
                              },
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("actions.delete")}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteAddress(address.id)}
                            sx={{
                              color: theme.palette.error.main,
                              "&:hover": {
                                backgroundColor: alpha(
                                  theme.palette.error.main,
                                  0.1
                                ),
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {!address.isDefault && (
                        <Tooltip title={t("delivery.setAsDefault")}>
                          <IconButton
                            size="small"
                            onClick={() => handleSetDefault(address.id)}
                            sx={{
                              color: theme.palette.warning.main,
                              "&:hover": {
                                backgroundColor: alpha(
                                  theme.palette.warning.main,
                                  0.1
                                ),
                              },
                            }}
                          >
                            <StarBorderIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add Address Dialog */}
      <Dialog
        dir={locale === LANG.AR ? "rtl" : "ltr"}
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <AddIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {t("delivery.addNewAddress")}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", mt: 2, flexDirection: "column", gap: 3 }}>
            <TextField
              label={t("profile.fields.nickName")}
              value={newAddress.fullName}
              onChange={(e) =>
                setNewAddress({ ...newAddress, fullName: e.target.value })
              }
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            <TextField
              label={t("company.fields.address")}
              value={newAddress.address}
              onChange={(e) =>
                setNewAddress({ ...newAddress, address: e.target.value })
              }
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            <FormControl fullWidth>
              <InputLabel>{t("company.fields.region")}</InputLabel>
              <Select
                value={newAddress.region}
                label={t("company.fields.region")}
                onChange={(e) =>
                  setNewAddress({ ...newAddress, region: e.target.value })
                }
                sx={{
                  borderRadius: 2,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                {regions.map((region) => (
                  <MenuItem
                    key={region.value}
                    value={region.value}
                    sx={{
                      py: 0.5,
                      minHeight: "auto",
                    }}
                  >
                    {region.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t("company.fields.city")}</InputLabel>
              <Select
                value={newAddress.city}
                label={t("company.fields.city")}
                onChange={(e) =>
                  setNewAddress({ ...newAddress, city: e.target.value })
                }
                sx={{
                  borderRadius: 2,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200,
                    },
                  },
                }}
              >
                {cities.map((city) => (
                  <MenuItem
                    key={city.value}
                    value={city.value}
                    sx={{
                      py: 0.5,
                      minHeight: "auto",
                    }}
                  >
                    {city.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t("profile.fields.phoneNumber")}
              value={newAddress.phoneNumber}
              onChange={(e) =>
                setNewAddress({ ...newAddress, phoneNumber: e.target.value })
              }
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setIsAddDialogOpen(false)}
            sx={{ borderRadius: 2 }}
          >
            {t("actions.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleAddAddress}
            sx={{ borderRadius: 2 }}
          >
            {t("actions.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Address Dialog */}
      <Dialog
        dir={locale === LANG.AR ? "rtl" : "ltr"}
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <EditIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {t("delivery.editAddress")}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {currentAddress && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TextField
                label={t("profile.fields.nickName")}
                value={currentAddress.fullName}
                onChange={(e) =>
                  setCurrentAddress({
                    ...currentAddress,
                    fullName: e.target.value,
                  })
                }
                fullWidth
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />

              <TextField
                label={t("company.fields.address")}
                value={currentAddress.address}
                onChange={(e) =>
                  setCurrentAddress({
                    ...currentAddress,
                    address: e.target.value,
                  })
                }
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />

              <FormControl fullWidth>
                <InputLabel>{t("company.fields.region")}</InputLabel>
                <Select
                  value={currentAddress.region}
                  label={t("company.fields.region")}
                  onChange={(e) =>
                    setCurrentAddress({
                      ...currentAddress,
                      region: e.target.value,
                    })
                  }
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  {regions.map((region) => (
                    <MenuItem key={region.value} value={region.value}>
                      {region.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t("company.fields.city")}</InputLabel>
                <Select
                  value={currentAddress.city}
                  label={t("company.fields.city")}
                  onChange={(e) =>
                    setCurrentAddress({
                      ...currentAddress,
                      city: e.target.value,
                    })
                  }
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  {cities.map((city) => (
                    <MenuItem key={city.value} value={city.value}>
                      {city.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label={t("profile.fields.phoneNumber")}
                value={currentAddress.phoneNumber}
                onChange={(e) =>
                  setCurrentAddress({
                    ...currentAddress,
                    phoneNumber: e.target.value,
                  })
                }
                fullWidth
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setIsEditDialogOpen(false)}
            sx={{ borderRadius: 2 }}
          >
            {t("actions.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleEditAddress}
            sx={{ borderRadius: 2 }}
          >
            {t("actions.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ShippingPage;
